import React, { useState, useEffect } from "react"
import { supabase } from "@/integrations/supabase/client"
import { Button } from "@/components/ui/button"
import { Heart } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { cn } from "@/lib/utils"

interface OptimizedLikeButtonProps {
  contentType: "image" | "video" | "music"
  contentId: string
  initialLikeCount: number
  initialIsLiked: boolean
  userId: string | null
  onLikeChange?: (isLiked: boolean, delta: number) => void
  className?: string
}

export function OptimizedLikeButton({ 
  contentType, 
  contentId, 
  initialLikeCount,
  initialIsLiked,
  userId,
  onLikeChange,
  className 
}: OptimizedLikeButtonProps) {
  const [isLiked, setIsLiked] = useState(initialIsLiked)
  const [likeCount, setLikeCount] = useState(initialLikeCount)
  const [isLoading, setIsLoading] = useState(false)
  const { toast } = useToast()

  // Update state when props change (e.g., when batch data loads)
  useEffect(() => {
    setLikeCount(initialLikeCount)
  }, [initialLikeCount])

  useEffect(() => {
    setIsLiked(initialIsLiked)
  }, [initialIsLiked])

  const handleLike = async (e: React.MouseEvent) => {
    e.stopPropagation()
    
    if (!userId) {
      toast({
        title: "Sign in required",
        description: "Please sign in to like content",
        variant: "destructive",
      })
      return
    }

    // Optimistic update
    const newIsLiked = !isLiked
    const delta = newIsLiked ? 1 : -1
    setIsLiked(newIsLiked)
    setLikeCount(prev => Math.max(0, prev + delta))
    onLikeChange?.(newIsLiked, delta)

    setIsLoading(true)
    try {
      if (!newIsLiked) {
        // Unlike
        const { error } = await supabase
          .from("content_likes")
          .delete()
          .eq("content_type", contentType)
          .eq("content_id", contentId)
          .eq("user_id", userId)

        if (error) throw error
      } else {
        // Like
        const { error } = await supabase
          .from("content_likes")
          .insert({
            content_type: contentType,
            content_id: contentId,
            user_id: userId,
          })

        if (error) throw error
      }
    } catch (error) {
      // Revert optimistic update on error
      console.error("Error toggling like:", error)
      setIsLiked(!newIsLiked)
      setLikeCount(prev => Math.max(0, prev - delta))
      onLikeChange?.(!newIsLiked, -delta)
      toast({
        title: "Error",
        description: "Failed to update like",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleLike}
      disabled={isLoading}
      className={cn(
        "gap-1.5 transition-all",
        isLiked && "text-red-500 hover:text-red-600",
        className
      )}
    >
      <Heart
        className={cn(
          "h-4 w-4 transition-all",
          isLiked && "fill-current"
        )}
      />
      <span className="text-xs font-medium">{likeCount}</span>
    </Button>
  )
}
